import type { AgentJobItem } from "./api";
import { getCompanyResearchReportIntentLabel, normalizeCompanyResearchIntentType } from "./companyResearch";
import { formatExecutiveMemoFailureMessage, readPendingExecutiveMemoJobSnapshot } from "./executiveMemos";
import { pickStringByPaths } from "./path";
import { resolveRememberedNewsJobMode } from "./newsJob";

export type HomeActivityLog = {
  id: string;
  text: string;
  time: string;
  activityAt?: string;
  to?: string;
  isActive?: boolean;
};

function normalizeString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function toHHMM(value: unknown): string {
  if (typeof value === "string" && /^\d{1,2}:\d{2}$/.test(value.trim())) return value.trim();
  if (typeof value === "string" || typeof value === "number") {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
    }
  }
  return "--:--";
}

function toLocalDateString(isoString: string): string {
  const date = new Date(isoString);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function getAgentJobString(item: AgentJobItem, paths: string[]): string | null {
  return pickStringByPaths(item, paths) ?? null;
}

function getAgentJobId(item: AgentJobItem): string | null {
  return getAgentJobString(item, ["jobId", "job_id", "id"]) ?? normalizeString(item.id);
}

function getAgentJobResource(item: AgentJobItem): string {
  return normalizeString(item.resource) ?? "unknown";
}

function normalizeAgentJobStatus(status: string): string {
  const normalized = status.trim().toLowerCase();
  if (normalized === "pending" || normalized === "requesting") return "accepted";
  if (normalized === "in_progress" || normalized === "runnning") return "running";
  if (normalized === "ambiguous" || normalized === "needs_more_info") return "needs_input";
  return normalized;
}

function getAgentJobStatus(item: AgentJobItem): string {
  return normalizeAgentJobStatus(getAgentJobString(item, ["status", "rawStatus", "raw_status"]) ?? "");
}

function isActiveAgentJobStatus(status: string): boolean {
  const normalized = normalizeAgentJobStatus(status);
  return normalized === "accepted" || normalized === "running";
}

export function getAgentJobActivityAt(item: AgentJobItem): string | null {
  return getAgentJobString(item, [
    "processedAt", "processed_at", "updatedAt", "updated_at", "requestedAt", "requested_at", "acceptedAt", "accepted_at",
  ]);
}

function getAgentJobDisplayTitle(item: AgentJobItem): string | null {
  return getAgentJobString(item, ["title"]);
}

function getAgentJobSummary(item: AgentJobItem): string | null {
  return getAgentJobString(item, ["summary", "resultSummary", "result_summary"]);
}

function getAgentJobErrorMessage(item: AgentJobItem): string | null {
  return getAgentJobString(item, ["errorMessage", "error_message"]);
}

function parseAgentJobMeta(item: AgentJobItem): Record<string, unknown> | null {
  if (!item.meta) return null;
  if (typeof item.meta === "object" && !Array.isArray(item.meta)) return item.meta as Record<string, unknown>;
  if (typeof item.meta !== "string") return null;
  try {
    const parsed = JSON.parse(item.meta) as unknown;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed as Record<string, unknown> : null;
  } catch {
    return null;
  }
}

function getCompanyResearchReportId(item: AgentJobItem): string | null {
  return pickStringByPaths(parseAgentJobMeta(item), ["latest_job_id", "latestJobId"]) ?? getAgentJobId(item);
}

function getCompanyResearchReportContext(item: AgentJobItem) {
  const meta = parseAgentJobMeta(item);
  const displayTitle = getAgentJobDisplayTitle(item);
  return {
    companyName: pickStringByPaths(meta, ["company_name", "companyName"])
      ?? (displayTitle && displayTitle !== "企業リサーチ" ? displayTitle : null),
    intentType: normalizeCompanyResearchIntentType(pickStringByPaths(meta, ["intent_type", "intentType"])),
  };
}

export function createHomeJobDetail(resource: string, item: AgentJobItem): string | null {
  if (resource === "company_research") return null;
  const status = getAgentJobStatus(item);
  const rawDetail = getAgentJobSummary(item) || getAgentJobErrorMessage(item);
  const detail = resource === "executive_memo" && status === "failed"
    ? formatExecutiveMemoFailureMessage(getAgentJobErrorMessage(item) || getAgentJobSummary(item))
    : rawDetail;
  if (!detail) return null;
  const normalized = detail.replace(/^#+\s*/gm, "").replace(/[-*]\s+/g, "").replace(/\s+/g, " ").trim();
  if (!normalized) return null;
  return normalized.length > 48 ? `${normalized.slice(0, 48)}...` : normalized;
}

function resolveAgentJobRoute(item: AgentJobItem): string | undefined {
  const jobId = getAgentJobId(item);
  if (getAgentJobResource(item) === "company_research" && jobId) {
    return `/request/company-research/jobs/${encodeURIComponent(jobId)}`;
  }
  return undefined;
}

export function resolveNewsModeLabel(item: AgentJobItem): "ニュース" | "レポート" {
  const meta = parseAgentJobMeta(item);
  const candidate = pickStringByPaths(item, [
    "mode", "newsMode", "news_mode", "payload.mode", "request.mode", "requestPayload.mode",
    "meta.mode", "meta.newsMode", "meta.news_mode", "meta.news.mode", "meta.request.mode", "meta.requestPayload.mode",
  ]) ?? pickStringByPaths(meta, ["mode", "newsMode", "news_mode", "news.mode", "request.mode", "requestPayload.mode"]) ?? "";
  const normalized = candidate.trim().toLowerCase();
  if (normalized === "report") return "レポート";
  if (normalized === "news") return "ニュース";
  if (getAgentJobResource(item) === "report") return "レポート";
  const summary = (getAgentJobSummary(item) ?? "").trim().toLowerCase();
  if (summary.includes("レポート") || summary.includes("report")) return "レポート";
  return resolveRememberedNewsJobMode(getAgentJobId(item)) === "report" ? "レポート" : "ニュース";
}

export function buildAgentJobTitle(
  resource: string,
  status: string,
  newsModeLabel: "ニュース" | "レポート" = "ニュース",
  displayTitle?: string | null,
  companyResearchIntent?: unknown
): string {
  const normalized = normalizeAgentJobStatus(status);
  if (resource === "receipt") {
    if (normalized === "completed") return "領収書の処理が完了しました";
    if (normalized === "failed") return "領収書の処理に失敗しました";
    if (normalized === "running") return "領収書を処理中です";
    if (normalized === "needs_input") return "領収書の処理に追加情報が必要です";
    return "領収書の処理を受け付けました";
  }
  if (resource === "news") {
    if (normalized === "completed") return `${newsModeLabel}送信の処理が完了しました`;
    if (normalized === "failed") return `${newsModeLabel}送信の処理に失敗しました`;
    if (normalized === "running") return `${newsModeLabel}を送信中です`;
    if (normalized === "needs_input") return `${newsModeLabel}送信に追加情報が必要です`;
    return `${newsModeLabel}送信の処理を受け付けました`;
  }
  if (resource === "report") {
    if (normalized === "completed") return "レポートの作成が完了しました";
    if (normalized === "failed") return "レポートの作成に失敗しました";
    if (normalized === "running") return "レポートを作成中です";
    if (normalized === "needs_input") return "レポート作成に追加情報が必要です";
    return "レポート作成を受け付けました";
  }
  if (resource === "company_research") {
    const companyName = displayTitle?.trim();
    const intentLabel = getCompanyResearchReportIntentLabel(companyResearchIntent);
    const actionLabel = companyResearchIntent === "learn_more" ? `「${intentLabel}」` : intentLabel;
    const label = companyName ? `${companyName}の${actionLabel}` : actionLabel;
    if (normalized === "completed") return `${label}が完了しました`;
    if (normalized === "failed") return `${label}に失敗しました`;
    if (normalized === "running") return companyResearchIntent ? `${label}を実行中です` : `${companyName ? `${companyName}の` : ""}企業情報を確認しています`;
    if (normalized === "needs_input") return `${label}に追加情報が必要です`;
    return `${label}を受け付けました`;
  }
  if (resource === "executive_memo") {
    if (normalized === "completed") return "社長メモを保存しました";
    if (normalized === "failed") return "社長メモを整理できませんでした";
    if (normalized === "running") return "社長メモを処理中です";
    if (normalized === "needs_input") return "社長メモの処理に追加情報が必要です";
    return "社長メモの処理を受け付けました";
  }
  if (normalized === "completed") return "処理が完了しました";
  if (normalized === "failed") return "処理に失敗しました";
  if (normalized === "running") return "処理中です";
  if (normalized === "needs_input") return "追加情報が必要です";
  return "処理を受け付けました";
}

export function agentJobItemToActivityLog(item: AgentJobItem, index: number): HomeActivityLog {
  const resource = getAgentJobResource(item);
  const status = getAgentJobStatus(item);
  const newsMode = resource === "news" || resource === "report" ? resolveNewsModeLabel(item) : "ニュース";
  const company = resource === "company_research" ? getCompanyResearchReportContext(item) : null;
  const title = buildAgentJobTitle(resource, status, newsMode, company?.companyName ?? getAgentJobDisplayTitle(item), company?.intentType);
  const detail = createHomeJobDetail(resource, item);
  const activityAt = getAgentJobActivityAt(item);
  return {
    id: resource === "company_research" ? getCompanyResearchReportId(item) ?? item.id ?? `job-${index}` : getAgentJobId(item) ?? item.id ?? `job-${index}`,
    text: detail ? `${title}\n${detail}` : title,
    time: toHHMM(activityAt),
    activityAt: activityAt ?? undefined,
    to: resolveAgentJobRoute(item),
    isActive: isActiveAgentJobStatus(status),
  };
}

export function groupAgentJobActivityLogsByDay(
  items: AgentJobItem[], referenceDate: Date = new Date()
): { todayLogs: HomeActivityLog[]; yesterdayLogs: HomeActivityLog[]; hasActiveJobs: boolean } {
  const today = toLocalDateString(referenceDate.toISOString());
  const yesterday = toLocalDateString(new Date(referenceDate.getTime() - 86400000).toISOString());
  const todayLogs: HomeActivityLog[] = [];
  const yesterdayLogs: HomeActivityLog[] = [];
  let hasActiveJobs = false;
  items.forEach((item, index) => {
    const activityAt = getAgentJobActivityAt(item);
    if (!activityAt) return;
    const log = agentJobItemToActivityLog(item, index);
    hasActiveJobs ||= log.isActive === true;
    const date = toLocalDateString(activityAt);
    if (date === today) todayLogs.push(log);
    else if (date === yesterday) yesterdayLogs.push(log);
  });
  return {
    todayLogs: mergeHomeActivityLogsByRecency(todayLogs),
    yesterdayLogs: mergeHomeActivityLogsByRecency(yesterdayLogs),
    hasActiveJobs,
  };
}

export function mergeHomeActivityLogsByRecency(...sources: HomeActivityLog[][]): HomeActivityLog[] {
  return sources.flat().map((log, index) => ({ log, index, timestamp: Date.parse(log.activityAt ?? "") }))
    .sort((left, right) => {
      const leftHasTimestamp = Number.isFinite(left.timestamp);
      const rightHasTimestamp = Number.isFinite(right.timestamp);
      if (leftHasTimestamp && rightHasTimestamp && left.timestamp !== right.timestamp) return right.timestamp - left.timestamp;
      if (leftHasTimestamp !== rightHasTimestamp) return leftHasTimestamp ? -1 : 1;
      return left.index - right.index;
    }).map(({ log }) => log);
}

export function addPendingExecutiveMemoReport(
  grouped: { todayLogs: HomeActivityLog[]; yesterdayLogs: HomeActivityLog[]; hasActiveJobs: boolean },
  referenceDate: Date = new Date()
) {
  const snapshot = readPendingExecutiveMemoJobSnapshot();
  if (!snapshot) return grouped;
  const log: HomeActivityLog = {
    id: `executive-memo-${snapshot.jobId}`,
    text: "社長メモを処理中です",
    time: toHHMM(snapshot.startedAt),
    activityAt: snapshot.startedAt,
    to: "/request/executive-memos",
    isActive: true,
  };
  const today = toLocalDateString(referenceDate.toISOString());
  const yesterday = toLocalDateString(new Date(referenceDate.getTime() - 86400000).toISOString());
  const date = toLocalDateString(snapshot.startedAt);
  return {
    todayLogs: date === today ? mergeHomeActivityLogsByRecency(grouped.todayLogs, [log]) : grouped.todayLogs,
    yesterdayLogs: date === yesterday ? mergeHomeActivityLogsByRecency(grouped.yesterdayLogs, [log]) : grouped.yesterdayLogs,
    hasActiveJobs: true,
  };
}
